import React, { useCallback, useMemo } from "react";
import { Col } from "reactstrap";
import cx from "classnames";
import type * as Scrivito from "scrivito";

import s from "components/ProductsOverview/ProductsOverview.module.scss";
import { Icon } from "components";
import type { IconName } from "components/Icon/iconNames";
import { useProductImage } from "hooks";
import { useScrivitoImage } from "hooks/productHooks/useScrivitoImage";
import type { ProductNode } from "interfaces/product/product.types";

const flagsEn: Record<string, IconName> = {
  Germany: "flag-de",
  Switzerland: "flag-ch",
  Spain: "flag-es",
  Unitedkingdom: "flag-uk",
  Southafrica: "flag-za",
  Czechrepublic: "flag-cz",
};
const flagsDe: Record<string, IconName> = {
  Deutschland: "flag-de",
  Schweiz: "flag-ch",
  Spanien: "flag-es",
  Vereinigteskoenigreich: "flag-uk",
  Suedafrika: "flag-za",
  Tschechien: "flag-cz",
};

const flags: Record<string, IconName> = { ...flagsEn, ...flagsDe };

type ProductCardProps = {
  isExtended: boolean;
  product: ProductNode;
  imageCardHandler: (product: ProductNode) => void;
  isSameProj: boolean;
};

export const ProductCard = ({
  isExtended,
  product,
  imageCardHandler,
  isSameProj,
}: ProductCardProps) => {
  // If pimId is present, get image url from pimId
  const { productImage, fetching } = useProductImage(product?.pimId ?? "");
  // else fallback to check if image obj is present and then get image url from obj.
  const { productImage: scrivitoProductImage, fetching: scrivitoFetching } =
    useScrivitoImage((!product?.pimId && product?.image) as Scrivito.Obj);

  const image = useMemo(
    () => ({
      url: productImage || scrivitoProductImage,
      fetching: fetching || scrivitoFetching,
    }),
    [fetching, productImage, scrivitoFetching, scrivitoProductImage],
  );

  const handleImageClick = useCallback(() => {
    imageCardHandler(product);
  }, [imageCardHandler, product]);

  return (
    <Col
      className={cx("col-xl-3 col-lg-3 col-md-6 col-sm-12 pt-2 px-2_5 mb-2", {
        "col-xl-4 col-lg-4": isExtended,
      })}
    >
      <button
        className={cx(
          s.imageCard,
          "w-100 d-flex flex-column justify-content-end align-items-center rounded-1 bg-white shadow-sm position-relative border-0 p-0",
        )}
        onClick={handleImageClick}
      >
        {isSameProj && product?.countryOfOrigin && (
          <Icon
            iconName={flags[product?.countryOfOrigin]}
            className="icon-24 position-absolute"
            style={{ top: "0.25rem", right: "0.25rem" }}
          />
        )}
        <div className={cx(s.imgDiv, "d-flex m-0 p-0 cursor-pointer")}>
          {image.url && !image.fetching && (
            <img src={image.url} className="m-0" alt={product.key} />
          )}
        </div>
        <div
          className={cx(
            s.cardFooter,
            "w-100 py-2_5 d-flex flex-row justify-content-start align-items-center ",
          )}
        >
          <p
            data-testid={`ProductCard_${product.name?.split(" ").join("")}`}
            className="text-left w-100 m-0 ps-2_5 text-blue-800 cursor-pointer text-break"
          >
            {product.name}
          </p>
        </div>
      </button>
    </Col>
  );
};
